import React from "react";
import FormProfile from "./FormProfile";
// import FormPassChange from "./FormPassChange";
import { useAuth } from "../context/AuthContext";
import { Button } from "@shadcn-in-nx/ui/button";
import {
    LogOut,
    Shield,
    ChevronDown,
    ChevronUp,
    Trash2,
    Copy,
    Check,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useConfirm } from "../hooks/useConfirm";
import { toast } from "sonner";

const UserProfile = () => {
    const { t } = useTranslation();
    const { user, isLoadingUser, getUser, logout } = useAuth();
    const [confirm, ConfirmDialog] = useConfirm();
    const [isLoggingOut, setIsLoggingOut] = React.useState(false);
    const [showSecuritySection, setShowSecuritySection] = React.useState(false);
    const [showDeletionEmail, setShowDeletionEmail] = React.useState(false);
    const [emailCopied, setEmailCopied] = React.useState(false);

    const SUPPORT_EMAIL = "support@agentui.ai";

    const handleLogoutAllSessions = async () => {
        try {
            await confirm({
                title: t("profile.logoutAllSessionsTitle"),
                description: t("profile.logoutAllSessionsDescription"),
                confirmText: t("profile.logoutAllSessionsConfirm"),
                cancelText: t("common.cancel"),
            });

            setIsLoggingOut(true);
            await logout(true); // Pass true to logout from all sessions
            toast.success(t("profile.logoutAllSessionsSuccess"));
        } catch (error) {
            // User cancelled or error occurred
            if (error) {
                console.error("Logout all sessions failed:", error);
                toast.error(t("profile.logoutAllSessionsError"));
            }
        } finally {
            setIsLoggingOut(false);
        }
    };

    const handleRequestDeletion = () => {
        setShowDeletionEmail(true);
    };

    const handleCopyEmail = async () => {
        try {
            await navigator.clipboard.writeText(SUPPORT_EMAIL);
            setEmailCopied(true);
            toast.success(t("profile.emailCopied"));
            setTimeout(() => setEmailCopied(false), 2000);
        } catch (err) {
            toast.error(t("profile.emailCopyFailed"));
        }
    };

    return (
        <div className="space-y-10">
            {isLoadingUser ? (
                <FormProfile isLoading={isLoadingUser} key={`loading`} />
            ) : (
                <>
                    <FormProfile user={user} refetchUser={getUser} />

                    {/* Security Section Toggle */}
                    <div className="space-y-4">
                        <button
                            type="button"
                            onClick={() =>
                                setShowSecuritySection(!showSecuritySection)
                            }
                            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
                        >
                            <Shield className="w-4 h-4" />
                            <span className="text-sm">
                                {t("profile.showSecurityOptions")}
                            </span>
                            {showSecuritySection ? (
                                <ChevronUp className="w-4 h-4" />
                            ) : (
                                <ChevronDown className="w-4 h-4" />
                            )}
                        </button>

                        {/* Security Section - Hidden by default */}
                        {showSecuritySection && (
                            <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-200">
                                <div className="flex items-center gap-2">
                                    <Shield className="w-5 h-5" />
                                    <h2 className="font-semibold text-xl">
                                        {t("profile.securityTitle")}
                                    </h2>
                                </div>

                                <div className="border rounded-lg p-4 space-y-6">
                                    {/* Logout All Sessions */}
                                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                                        <div>
                                            <h3 className="font-medium">
                                                {t("profile.activeSessions")}
                                            </h3>
                                            <p className="text-sm text-muted-foreground">
                                                {t(
                                                    "profile.activeSessionsDescription"
                                                )}
                                            </p>
                                        </div>
                                        <Button
                                            variant="destructive"
                                            onClick={handleLogoutAllSessions}
                                            disabled={isLoggingOut}
                                        >
                                            <LogOut className="w-4 h-4 mr-2" />
                                            {isLoggingOut
                                                ? t("common.loading")
                                                : t(
                                                      "profile.logoutAllSessions"
                                                  )}
                                        </Button>
                                    </div>

                                    <div className="border-t" />

                                    {/* Request Profile Deletion */}
                                    <div className="flex flex-col gap-4">
                                        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                                            <div>
                                                <h3 className="font-medium">
                                                    {t("profile.deleteAccount")}
                                                </h3>
                                                <p className="text-sm text-muted-foreground">
                                                    {t(
                                                        "profile.deleteAccountDescription"
                                                    )}
                                                </p>
                                            </div>
                                            {!showDeletionEmail && (
                                                <Button
                                                    variant="outline"
                                                    className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                                    onClick={
                                                        handleRequestDeletion
                                                    }
                                                >
                                                    <Trash2 className="w-4 h-4 mr-2" />
                                                    {t(
                                                        "profile.requestDeletion"
                                                    )}
                                                </Button>
                                            )}
                                        </div>

                                        {/* Show email after clicking button */}
                                        {showDeletionEmail && (
                                            <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                                                <p className="text-sm">
                                                    {t(
                                                        "profile.deletionEmailInstructions"
                                                    )}
                                                </p>
                                                <div className="flex items-center gap-2">
                                                    <code className="bg-background px-3 py-2 rounded border text-sm flex-1">
                                                        {SUPPORT_EMAIL}
                                                    </code>
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={
                                                            handleCopyEmail
                                                        }
                                                    >
                                                        {emailCopied ? (
                                                            <Check className="w-4 h-4 text-green-500" />
                                                        ) : (
                                                            <Copy className="w-4 h-4" />
                                                        )}
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}
            <ConfirmDialog />
        </div>
    );
};

export default UserProfile;
